import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { nasAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import { pageHidden } from '../../components/Sidebar/menuConfig';
import DataTable from '../../components/DataTable/DataTable';
import { guestIdentity, guestDetails } from '../../utils/guest';
import './Dashboard.css';

const GATEWAY_LABEL = { mikrotik: 'MikroTik', ruijie: 'Ruijie', nomadix: 'Nomadix', grandstream: 'Grandstream', unifi: 'UniFi', ubiquiti: 'Ubiquiti', cisco: 'Cisco', ruckus: 'Ruckus', aruba: 'Aruba', huawei: 'Huawei' };
const REFRESH_MS = 30 * 1000;

const formatBytes = (b) => {
    if (!b) return '0 MB';
    if (b >= 1099511627776) return `${(b / 1099511627776).toFixed(1)} TB`;
    if (b >= 1073741824)    return `${(b / 1073741824).toFixed(1)} GB`;
    if (b >= 1048576)       return `${(b / 1048576).toFixed(1)} MB`;
    return `${(b / 1024).toFixed(1)} KB`;
};
const formatDay = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const guestText = (g) => [guestIdentity(g)?.label, ...guestDetails(g)].filter(Boolean).join(' ');

// Who this is: the guest's name (else their room, else their mobile), with the room and
// mobile under it; where no guest is known, what they signed in as (a voucher code, say).
// The sign-in name itself stays on hover. For a device not signed in: when it last was.
function GuestCell({ guest, user, on }) {
    const label = guestIdentity(guest)?.label;
    const details = [...guestDetails(guest), on && `last on ${formatDay(on)}`].filter(Boolean);
    return (
        <span className="td-guest" title={user && label ? `Signed in as ${user}` : undefined}>
            <strong>{label || user || '—'}</strong>
            {details.length > 0 && <small>{details.join(' · ')}</small>}
        </span>
    );
}

const gatewayColumn = (rows) => {
    const names = [...new Set(rows.map((r) => r.gateway))];
    return { label: 'Gateway', field: 'gateway', ...(names.length > 1 ? { filter: { options: names } } : {}) };
};

/**
 * Who is online now, gateway by gateway (GET /api/nas/online): a MikroTik with its
 * RouterOS API on says it itself, Ruijie and the others from their RADIUS accounting.
 * A live router also says who is connected but not signed in (most are on the sign-in
 * page), each with the guest that device last signed in as at the branch.
 * A card of its own on the dashboards, beside their sessions; it shows nothing where
 * there is no gateway (or no Sessions → View).
 */
export default function OnlineNowCard({ branchId, tenantId }) {
    const access = useAuthStore((state) => state.access);
    const [online, setOnline] = useState(null);
    const [view, setView] = useState('online');

    const load = useCallback(async (fresh = false) => {
        try {
            const r = await nasAPI.getOnline({ branchId, tenantId, fresh });
            setOnline(r.data.data);
        } catch (_) { /* nothing to show */ }
    }, [branchId, tenantId]);

    useEffect(() => {
        setOnline(null);
        load();
        const t = setInterval(() => load(), REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    const gateways = online?.gateways || [];
    if (!gateways.length) return null;
    const knowsWaiting = online.waitingTotal != null;
    const showing = knowsWaiting ? view : 'online';

    const rows = gateways.flatMap((g) => g.users.map((u, i) => ({
        ...u,
        id: `${g.nasId}-${u.macAddress || u.user}-${i}`,
        gateway: g.name,
        guestName: guestText(u.guest),
        bytes: (u.download || 0) + (u.upload || 0),
    })));
    const waitingRows = gateways.flatMap((g) => (g.waiting || []).map((h, i) => ({
        ...h,
        id: `${g.nasId}-waiting-${h.macAddress}-${i}`,
        gateway: g.name,
        guestName: guestText(h.lastGuest),
    })));

    const columns = [
        { label: 'Guest', field: 'guestName', render: (r) => <GuestCell guest={r.guest} user={r.user} />, searchValue: (r) => `${r.guestName} ${r.user}` },
        { label: 'IP Addr', field: 'address' },
        { label: 'MAC ID', field: 'macAddress' },
        gatewayColumn(rows),
        { label: 'Online for', field: 'uptimeSec', render: (r) => r.uptime || '—' },
        {
            label: 'Data', field: 'bytes',
            render: (r) => <span title={`Download ${formatBytes(r.download)} · upload ${formatBytes(r.upload)}`}>↓ {formatBytes(r.download)} · ↑ {formatBytes(r.upload)}</span>,
        },
    ];
    const waitingColumns = [
        {
            label: 'Last signed in as', field: 'guestName',
            render: (r) => (r.lastGuest ? <GuestCell guest={r.lastGuest} on={r.lastGuest.at} /> : <span className="td-muted-cell">Not signed in here before</span>),
        },
        { label: 'MAC ID', field: 'macAddress' },
        { label: 'IP Addr', field: 'address' },
        gatewayColumn(waitingRows),
        { label: 'Connected for', field: 'uptimeSec', render: (r) => r.uptime || '—' },
    ];

    return (
        <div className="ad-card" style={{ marginTop: '1.5rem' }}>
            <div className="ad-card-header">
                <div className="ad-card-title td-online-title">
                    <i className="fas fa-wifi"></i>
                    <h2>Online now on gateways</h2>
                    <span className="ad-live-badge" title="Signed in on the gateways now">
                        <span className="ad-pulse-dot"></span>
                        {online.total} online now
                    </span>
                    {knowsWaiting && (
                        <span className="ad-waiting-badge" title="Connected to the Wi-Fi but not signed in (most are on the sign-in page)">
                            <i className="fas fa-hourglass-half" aria-hidden="true"></i>
                            {online.waitingTotal} not signed in
                        </span>
                    )}
                </div>
                <button type="button" className="ad-refresh-small" onClick={() => load(true)}>
                    <i className="fas fa-sync-alt"></i> Refresh
                </button>
            </div>
            <div style={{ padding: '1.125rem 1.375rem' }}>
                <ul className="td-gateways" aria-label="Gateways">
                    {gateways.map((g) => (
                        <li key={g.nasId} className="td-gateway">
                            <i className={`fas ${g.type === 'mikrotik' ? 'fa-server' : 'fa-wifi'}`} aria-hidden="true"></i>
                            <span className="td-gateway-name">
                                {g.name}
                                <small>{[GATEWAY_LABEL[g.type] || g.type, g.branch].filter(Boolean).join(' · ')}</small>
                            </span>
                            <span className="td-gateway-counts">
                                <span className="td-gateway-count"><strong>{g.count}</strong> online</span>
                                {g.waitingCount != null && <span className="td-gateway-waiting">{g.waitingCount} not signed in</span>}
                            </span>
                            <span className={`td-source td-source-${g.source}`}>
                                {g.source === 'router' ? 'Live from the router' : 'From RADIUS accounting'}
                            </span>
                            {g.routerError && (
                                <span className="td-badge-offline" title={g.routerError}>
                                    <i className="fas fa-circle-xmark"></i> Router not answering
                                </span>
                            )}
                            {g.apiOff && !pageHidden('/nas', access) && (
                                <Link className="td-link" to="/nas">Turn on its RouterOS API for a live list</Link>
                            )}
                        </li>
                    ))}
                </ul>
                {knowsWaiting && (
                    <div className="td-online-tools td-online-switch" role="group" aria-label="Which devices">
                        <button type="button" className="td-chip" aria-pressed={showing === 'online'} onClick={() => setView('online')}>
                            Signed in <strong>{online.total}</strong>
                        </button>
                        <button type="button" className="td-chip" aria-pressed={showing === 'waiting'} onClick={() => setView('waiting')}>
                            Connected, not signed in <strong>{online.waitingTotal}</strong>
                        </button>
                    </div>
                )}
                {showing === 'waiting' ? (
                    <DataTable key="waiting" columns={waitingColumns} rows={waitingRows} emptyMessage="Every device on the live routers has signed in" />
                ) : (
                    <DataTable key="online" columns={columns} rows={rows} emptyMessage="No one is online on the gateways right now" />
                )}
                <p className="td-online-note">
                    MikroTik with its RouterOS API on: live from the router, with the devices connected but not signed in (most are on the
                    sign-in page). Ruijie and the other gateways: from their RADIUS accounting (sessions they have not stopped), which
                    cannot say who has not signed in.{online.at && ` As of ${new Date(online.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })}.`}
                </p>
            </div>
        </div>
    );
}
